import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import type { PropsWithChildren } from 'react';
import { ConfigProvider } from 'antd';
import ruRU from 'antd/locale/ru_RU';
// Первый день недели в календаре AntD берёт не из своей локали, а из локали
// dayjs (`localeData().firstDayOfWeek()` для 'ru'). Без этого импорта локаль
// 'ru' в dayjs не зарегистрирована, dayjs падает на 'en' — и неделя в выборе
// даты начинается с воскресенья. Импорт только регистрирует локаль, глобальную
// не переключает: форматирование дат в остальном коде не меняется.
import 'dayjs/locale/ru';
import { getAntdTheme, type ThemeMode } from './tokens.ts';
import {
  getTableFontSize,
  subscribeUiFontSize,
  FONT_SCALE_BASELINE,
} from '../prefs/store.ts';

// AntD берёт названия месяцев для заголовка календаря и сетки выбора месяца из
// `DatePicker.lang.shortMonths` — по умолчанию это сокращения «Янв…Дек», из-за
// чего в шапке было «Июн 2026». Переопределяем полными названиями (с заглавной),
// чтобы календарь показывал «Июнь 2026». Заголовок и сетка используют один и тот
// же источник, поэтому меняем в одном месте.
const FULL_MONTHS = [
  'Январь', 'Февраль', 'Март', 'Апрель', 'Май', 'Июнь',
  'Июль', 'Август', 'Сентябрь', 'Октябрь', 'Ноябрь', 'Декабрь',
];

const localeRu = {
  ...ruRU,
  DatePicker: {
    ...ruRU.DatePicker!,
    lang: { ...ruRU.DatePicker!.lang, shortMonths: FULL_MONTHS },
  },
};

/**
 * Провайдер темы Lens. Хранит режим (light/dark) в localStorage, проставляет
 * `data-theme` на <html> (для CSS-переменных в global.css) и оборачивает
 * приложение в AntD ConfigProvider с нужным ThemeConfig.
 *
 * Светлая тема — дефолт. Тёмная включается переключателем (useThemeMode).
 */

const LS_THEME = 'lens:theme';

interface ThemeContextValue {
  mode: ThemeMode;
  toggle: () => void;
  setMode: (m: ThemeMode) => void;
}

const ThemeContext = createContext<ThemeContextValue | null>(null);

function readInitialMode(): ThemeMode {
  try {
    const v = localStorage.getItem(LS_THEME);
    if (v === 'dark' || v === 'light') return v;
  } catch {
    /* ignore */
  }
  return 'light';
}

// Применяем атрибут до первого рендера, чтобы не было вспышки светлой темы.
if (typeof document !== 'undefined') {
  document.documentElement.dataset.theme = readInitialMode();
}

export function ThemeProvider({ children }: PropsWithChildren): JSX.Element {
  const [mode, setModeState] = useState<ThemeMode>(() => readInitialMode());
  // Размер шрифта интерфейса из профиля. Подписываемся на его изменение, чтобы
  // AntD-тема пересобиралась при смене размера в профиле и при гидрации с сервера.
  const [uiFontSize, setUiFontSize] = useState<number>(() => getTableFontSize());

  useEffect(() => {
    document.documentElement.dataset.theme = mode;
    try {
      localStorage.setItem(LS_THEME, mode);
    } catch {
      /* ignore */
    }
  }, [mode]);

  useEffect(() => subscribeUiFontSize(() => setUiFontSize(getTableFontSize())), []);

  const setMode = useCallback((m: ThemeMode) => setModeState(m), []);
  const toggle = useCallback(() => setModeState((m) => (m === 'dark' ? 'light' : 'dark')), []);

  const ctx = useMemo<ThemeContextValue>(() => ({ mode, toggle, setMode }), [mode, toggle, setMode]);
  // Базовый размер шрифта AntD (14px) масштабируем множителем size/эталон,
  // где эталон = FONT_SCALE_BASELINE (13 → scale 1, исходный вид).
  const baseFontSize = useMemo(
    () => Math.round((14 * uiFontSize) / FONT_SCALE_BASELINE),
    [uiFontSize],
  );
  const themeConfig = useMemo(() => getAntdTheme(mode, baseFontSize), [mode, baseFontSize]);

  return (
    <ThemeContext.Provider value={ctx}>
      <ConfigProvider locale={localeRu} theme={themeConfig}>
        {children}
      </ConfigProvider>
    </ThemeContext.Provider>
  );
}

export function useThemeMode(): ThemeContextValue {
  const ctx = useContext(ThemeContext);
  if (!ctx) throw new Error('useThemeMode must be used within ThemeProvider');
  return ctx;
}
